import { List, SaveButton } from '@refinedev/antd';
import {
  Alert,
  Button,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Space,
  Tag,
  Typography,
} from 'antd';

import {
  IResourceComponentsProps,
  useCustom,
  useCustomMutation,
  useGetIdentity,
  useGetLocale,
  useTranslate,
  useWarnAboutChange,
} from '@refinedev/core';

import type { RcFile, UploadFile } from 'antd/lib/upload/interface';
import { enUS, ru } from 'date-fns/locale';
import { Line, LineConfig } from '@ant-design/charts';
import {
  ArrowLeftOutlined,
  BulbOutlined,
  EditOutlined,
  HourglassOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  emptyExposition,
  emptySud,
  situationInitialValues,
  UserRole,
} from '@cc/schema';
import {
  deepOmit,
  formatDurationHms,
  formatTimestampToReadableString,
  fromDurationFields,
  generateToken,
  sanitizeDurationInput,
  toDurationFields,
} from '@cc/utils';
import {
  Breadcrumb,
  Col,
  Drawer,
  message,
  notification,
  Row,
  Select,
  Slider,
  Table,
  Tooltip,
  Upload,
} from 'antd';
import * as Sentry from '@sentry/react';
import ImgCrop from 'antd-img-crop';
import { useWatch } from 'antd/lib/form/Form';
import {
  addSeconds,
  differenceInSeconds,
  format,
  formatDuration,
  intervalToDuration,
  parseISO,
  set,
} from 'date-fns';
import {
  deleteObject,
  getDownloadURL,
  getStorage,
  ref,
  uploadBytes,
} from 'firebase/storage';
import { flatten } from 'lodash';
import qs from 'qs';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useWindowSize } from 'rooks';
import { DateFnsTimePicker } from '../../components/Pickers';
import { useClientRoster } from '../../hooks/client-options';
import { IUser } from '../../providers';
import {
  Exposure,
  ExposureStimulus,
  GenerateDalleImageResponse,
} from '../../schema/be-graphql-generated';
import { useActivityTracking } from '../../hooks/use-activity-tracking';
import { useActor } from '@xstate/react';
import { TherapistActivityReactContext } from '../../App';
import { TherapistActivityService } from '../../machines/therapistActivityMachine';
import { useClientEra } from '../../hooks/useClientEra';
import { graphqlErrorInfo } from '../../utils/graphqlErrors';
import { formatDateTimeShort } from '../../utils/dateFormat';
import {
  canEditExposure,
  COEXISTENCE_COPY_KEYS,
  endSud,
  EXPOSURE_DOMAIN,
  expositionHandle,
  highestSud,
  isLogOwnedExposure,
  isLogOwnedStimulus,
  isRefusedAnswer,
  reactionHandle,
  refusedAnswerError,
  runnerUsesEs,
  runDurationSeconds,
  runsOf,
  wasAmended,
  type Exposition,
  type RunPoint,
  expectedCellText,
  hasExpectancyAnswers,
} from './esHelpers';
import {
  isEraMismatch,
  isEraSignal,
  useExpositionEsCommands,
  type ExpositionResult,
} from './useExpositionEsCommands';
import { ExpositionLikelihoodField } from './ExpositionLikelihoodField';
import useStyles from './styles.style';
// E43 — health data: the client's heart rate during an exposition (E21 · PortalExpositionHeartRate)
import { ExpositionHeartRateCell } from '../health/ExpositionHeartRateCell';

const { Text, Title } = Typography;

export const EditExposureSituation: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const { i18n } = useTranslation();

  const { styles } = useStyles();

  const location = useLocation();

  const lastGeneratedImageUrlRef = useRef<string | null>(null);

  const therapistActivityService = useContext(TherapistActivityReactContext);
  const [therapistActivityState, therapistActivitySend] = useActor(
    therapistActivityService as TherapistActivityService,
  );

  const { innerWidth } = useWindowSize();

  const {
    clientId: locationClientId,
    exposureStimulusId: savedExposureStimulusId,
  } =
    useMemo(
      () =>
        qs.parse(
          location.search.charAt(0) === '?'
            ? location.search.slice(1)
            : location.search,
        ),
      [location.search],
    ) || {};

  const [exposureStimulusId, setExposureStimulusId] = useState(
    savedExposureStimulusId as string,
  );

  const [clientId, setClientId] = useState<string | undefined>(
    locationClientId as string,
  );

  useActivityTracking(exposureStimulusId);

  const [clientUid, setClienttUid] = useState<string | undefined>();

  useEffect(() => {
    therapistActivitySend({
      type: 'ENTITY_CLIENT_ID_CHANGED',
      clientId,
    });
  }, [clientId]);

  const {
    data: identity,
    remove,
    refetch: refetchIdentity,
  } = useGetIdentity<IUser>();

  const [files, setFiles] = useState<UploadFile[]>([]);

  const [expositionIdx, setExpositionIdx] = useState(0);

  const {
    data: exposureStimulusData,
    isLoading: exposureStimulusLoading,
    refetch: refetchStimulusExposure,
    error: exposureStimulusError,
  } = useCustom<ExposureStimulus>({
    url: '',
    method: 'get',
    meta: {
      operation: 'exposureStimulus',
      fields: [
        '_id',
        // THE ROW DECIDES THE ERA (T95a): a rung the converter has not stamped keeps the legacy
        // runner, because the exposition commands refuse one (EXPOSURE_NOT_CONVERTED)
        'exposureStimulusId',
        'rank',
        'title',
        'description',
        'exposureId',
        'imageUrls',
        'imagePrompt',
        'sud',
        'avoidance',
        'worstThingThatCouldHappen',
        {
          expositions: [
            // E20 WP2's additive read-only handles — the runner cannot address a live run without
            // `expositionId`, and `duration` is what FinishExposition recorded
            'expositionId',
            'duration',
            'createdAt',
            'anticipatedSud',
            'reactionNotes',
            // D-expectancies-2026-10-07 — null on an older exposition ("not asked")
            'expectedOutcome',
            'expectedLikelihood',
            'outcomeOccurred',
            'likelihoodAfter',
            'learned',
            {
              /**
               * `reactionId` and `amendedFrom` are ADDITIVE and both are needed HERE, not only on
               * the mutation payloads (the amend slice). Without them a page LOAD — as opposed to
               * a run recorded in this session — gives the drawer points with no handle, so a
               * correction falls back to addressing the point by its POSITION and the projection
               * then stamps that derived handle over the stable one the server minted; and a point
               * corrected in an earlier session shows no "corrected" marker at all, because the
               * original reading never arrived. Found by driving the real UI (T99): the second
               * correction of one point looked like a correction of a different point.
               */
              suds: [
                'sud',
                'reactedAt',
                'reactionId',
                { amendedFrom: ['sud', 'reactedAt'] },
              ],
            },
          ],
        },
      ],

      variables: {
        _id: {
          name: '_id',
          type: 'ID',
          value: exposureStimulusId,
        },
      },
    },
  });

  const [exposureId, setExposureId] = useState('');

  const [openDrawer, setOpenDrawer] = useState(false);

  const {
    data: exposureData,
    isLoading: exposureLoading,
    refetch: refetchExposure,
    error: exposureError,
  } = useCustom<Exposure>({
    url: '',
    method: 'get',
    meta: {
      operation: 'exposure',
      // `exposureId` is the LADDER's ES identity — BOTH halves of the parent–child pair must be
      // log-owned before a run may be recorded through the ES commands
      fields: ['userId', 'exposureId', 'safetyBehaviorsToAvoid'],
      variables: {
        _id: {
          name: '_id',
          type: 'ID',
          value: exposureId,
        },
      },
    },
  });

  // the WHOLE practice roster (P011) — this page needs the client's `uid` (the storage prefix) and
  // its edit gate reads `access` off the same row
  const { clients, refetchClients } = useClientRoster();

  useEffect(() => {
    const getClientUidData = async () => {
      if (exposureId && !clientUid) {
        const res = await refetchExposure();
        if (res.data?.data?.userId) {
          setClientId(res.data?.data?.userId);

          // the roster is normally already in cache; if this page opened before it arrived, ask for
          // it now — `refetchClients` hands back the clients themselves, not the transport envelope
          const clientsToUse = clients.length
            ? clients
            : await refetchClients();

          const uid = clientsToUse.find(
            (c) => c._id === res.data?.data?.userId,
          )?.uid;

          if (uid) {
            setClienttUid(uid);
          }
        }
      }
    };
    getClientUidData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exposureId, !clients.length]);

  const imagePrefix = `tools/${clientUid || ''}/exposureStimuli/${(
    (exposureStimulusId as string) || ''
  ).replace('exposureStimuli/', '')}/`;

  /**
   * The CLIENT's era for this domain — never the therapist's (plan 07 C3). Re-asked on every mount
   * and after a coexistence refusal (T112: a flip can land while this page is open).
   */
  const { isEsClient, refetchEra } = useClientEra(clientId, EXPOSURE_DOMAIN);

  /**
   * May the RUNNER write through the ES commands? Both halves of the parent–child pair have to be
   * log-owned: the ladder is the DCB boundary and the rung is what the run hangs off, so a
   * converted ladder with an unconverted rung still routes legacy — which is exactly what the
   * resolver's `resolveRung` says with `EXPOSURE_NOT_CONVERTED`.
   */
  const esRunner = runnerUsesEs({
    isEsClient,
    exposure: exposureData?.data ?? null,
    stimulus: exposureStimulusData?.data ?? null,
  });

  /**
   * BOTH HALVES OF THE PAIR HAVE ANSWERED. The era of a RUNG is a fact about two rows — the rung's
   * own identity and its ladder's — and it is not knowable until both queries have come back for
   * the ids they were asked about (review 2026-09-05, D3). This used to read the rung alone, so on
   * a CONVERTED rung the "still being moved" notice appeared for one round trip while the exposure
   * query was still in flight and then vanished: a warning about a state that was never true.
   *
   * `exposureId` is learnt FROM the rung, so an empty one means there is nothing more to wait for
   * (a rung with no parent FK — not a shape prod holds, but the honest reading is "the ladder is
   * not there", which is what the notice would then say).
   */
  const eraKnown =
    exposureStimulusData !== undefined &&
    (exposureId ? exposureData !== undefined : true);

  /** `es` client, but this rung has not been moved across yet — said out loud, not hidden. */
  const unconvertedRung =
    isEsClient &&
    eraKnown &&
    !!exposureStimulusData?.data &&
    !(
      isLogOwnedStimulus(exposureStimulusData?.data) &&
      isLogOwnedExposure(exposureData?.data)
    );

  const { setWarnWhen } = useWarnAboutChange();

  /**
   * Report a failed write in three tiers — the coexistence codes in the THERAPIST's voice:
   *   1. a COEXISTENCE code, re-voiced — the server's copy addresses the account owner;
   *   2. any other coded rejection — the server's own localized message, verbatim;
   *   3. anything else — the tool's generic save error, and Sentry.
   *
   * Tier 3 files a Sentry event, exactly as `create.tsx` does (review 2026-09-05, D7). The two
   * editors had drifted: an uncoded failure in the SITUATION editor — the one that drives every
   * exposition write — was the only save path in the tool that failed invisibly to us. A coded
   * rejection is a decision the server made and does not belong in Sentry; anything else is a bug
   * or an outage, and that is precisely what should be reported.
   */
  const reportFailure = useCallback(
    (error: unknown, fallbackKey = 'shared.exposure.saveError') => {
      const { code, message: serverMessage } = graphqlErrorInfo(error);
      const therapistCopyKey = code ? COEXISTENCE_COPY_KEYS[code] : undefined;
      if (therapistCopyKey) {
        message.error(t(therapistCopyKey));
        return;
      }
      if (code && serverMessage) {
        message.error(serverMessage);
        return;
      }
      message.error(t(fallbackKey));
      Sentry.captureException(error);
    },
    [t],
  );

  const [isStopwatchRunning, setIsStopwatchRunning] = useState<boolean>(false);
  const [time, setTime] = useState<number>(0);

  /**
   * THE E-ERA RUN, and the one piece of state the legacy drawer never needed.
   *
   * In the legacy era a run is a record the drawer BUILDS and posts whole on Save. From E20 WP2 it
   * is a sequence of durable facts, so the drawer has to hold the run's HANDLE — the thing
   * `recordExpositionReaction` and `finishExposition` address — and `startExposition` is what mints
   * it. `null` means "no run open in the log yet": the drawer is showing an empty form and the
   * first press of Start is what creates one.
   */
  const [esExpositionId, setEsExpositionId] = useState<string | null>(null);
  /**
   * The level the NEXT reaction will record. In the legacy drawer a tap appended a point carrying
   * the previous SUD and the therapist dragged it afterwards; a recorded reaction is a fact no E20
   * slice can amend, so the E-era runner asks for the level BEFORE the tap (the same call WP4 made
   * on mobile, for the same reason, so the two surfaces behave alike).
   */
  const [currentSud, setCurrentSud] = useState<number>(50);
  /** The run the drawer is showing, as the SERVER last answered — never a local guess. */
  const [esRun, setEsRun] = useState<Exposition | null>(null);

  /**
   * How far into the run a reading sits, in whole seconds — the number BOTH surfaces show. The
   * anchor is the run's own start, falling back to its first reading for a row that only ever
   * carried readings, which is exactly the rule the app uses (`getReactionBaseTimestamp`).
   */
  const elapsedSecondsOfPoint = useCallback(
    (run: Exposition | null, reactedAt?: string | null): number => {
      const anchor = run?.createdAt || run?.suds?.[0]?.reactedAt;
      if (!anchor || !reactedAt) return 0;
      return Math.max(
        0,
        differenceInSeconds(
          parseISO(String(reactedAt)),
          parseISO(String(anchor)),
        ),
      );
    },
    [],
  );
  /** A run reopened from the history table: add reactions and finish again (never amend). */
  const [esResuming, setEsResuming] = useState(false);
  /**
   * Is the LEGACY drawer recording a run the row does not hold yet, or editing one it does?
   *
   * It matters only when a flip lands mid-drawer (T112). A NEW run is expressible in the E-era
   * vocabulary exactly — `StartExposition` → N × `RecordExpositionReaction` → `FinishExposition` —
   * so the refused legacy save can be re-issued and the therapist's work LANDS. An edit of a run
   * the log already holds is not: amending a recorded point's level or its clock has no modelled
   * command (registry `open` → `discard-a-run`), and inventing one here would be inventing history.
   * The two cases therefore get two different, and equally loud, answers.
   */
  const [legacyRunIsNew, setLegacyRunIsNew] = useState(true);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (isStopwatchRunning) {
      interval = setInterval(() => {
        setTime((prevTime) => prevTime + 1000);
      }, 1000);
    } else {
      interval && clearInterval(interval);
    }

    return () => {
      interval && clearInterval(interval);
    };
  }, [isStopwatchRunning]);

  const {
    mutate: upsertExposureStimulus,
    data: upsertExposureStimulusData,
    isLoading: upsertExposureStimulusLoading,
    error: upsertExposureStimulusError,
  } = useCustomMutation<{ exposureStimulus: ExposureStimulus }>();

  const memoizedAvoidanceOptions = useMemo(() => {
    return t('shared.exposure.avoidances', {
      returnObjects: true,
      // @ts-ignore
    }).map((avoidance: string, index: number) => (
      <Select.Option
        key={index}
        value={index}
        data-testid="cc-create-exposure-situation-avoidance-option"
      >
        {avoidance}
      </Select.Option>
    ));
  }, [i18n.language]);

  const [exposureForm] = Form.useForm<typeof emptyExposition>();

  const [form] = Form.useForm<typeof situationInitialValues>();

  const imagePrompt = useWatch('imagePrompt', form);

  useEffect(() => {
    if (exposureStimulusData?.data?._id) {
      setExposureStimulusId(exposureStimulusData?.data?._id);
      setExposureId(exposureStimulusData?.data?.exposureId || '');

      const { _id, createdAt, updatedAt, __typename, userId, ...rest } =
        deepOmit(exposureStimulusData?.data || {}, ['__typename']);
      form.setFieldsValue({
        ...rest,
      });

      setFiles(
        (exposureStimulusData?.data?.imageUrls || []).map((url, idx) => ({
          uid: String(idx + 1),
          name: `photo-${idx + 1}.png`,
          status: 'done',
          // @ts-ignore
          url: url || '',
        })),
      );
    }
  }, [exposureStimulusData]);

  const [imageLoading, setImageLoading] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const {
    mutate: generateAiImage,
    data: generateAiImageData,
    isLoading: generateAiImageLoading,
    error: generateAiImageError,
  } = useCustomMutation<GenerateDalleImageResponse>();

  const getLocale = useGetLocale();
  const currentLocale = useMemo(() => getLocale(), [i18n.languages[0]]);

  /**
   * ON-BEHALF IMAGE GENERATION (E21 WP5). `subject` and `exposureId` are both ADDITIVE and both
   * fix real gaps rather than adding features:
   *
   *   • `subject` — this mutation judged the era from `user._id`, which on the portal is the
   *     THERAPIST. A therapist is never an `es` subject, so every portal generation for an `es`
   *     client silently took the legacy path and the log recorded nothing at all. That is T99 (a)
   *     exactly ("the therapist-on-behalf branch had no ES routing"), the defect the founder found
   *     by hand in E10, in a second place.
   *   • `exposureId` — the ladder the prompt belongs to, which `DalleImageRequested` names on the
   *     board and the shipped mutation never carried.
   */
  const generateNewImage = async () => {
    await generateAiImage({
      url: '',
      method: 'post',
      values: {},
      meta: {
        operation: 'generateDalleImage',
        fields: ['contentUrl', 'status', 'errorMessage', 'code'],
        variables: {
          content: {
            name: 'content',
            type: 'String!',
            value: imagePrompt,
          },
          lng: {
            name: 'lng',
            type: 'String!',
            value: currentLocale?.startsWith('ru') ? 'ru' : 'en',
          },
          subject: {
            name: 'subject',
            type: 'ID',
            value: clientId,
          },
          exposureId: {
            name: 'exposureId',
            type: 'ID',
            value: exposureId,
          },
        },
      },
    });
  };

  useEffect(() => {
    const processImage = async () => {
      if (generateAiImageData?.data) {
        /**
         * PATCHLOG P003 (E21 `dead-content-filter`, founder G1 2026-08-29): this branch checked
         * `code === 'ContentFilter'` and the server has never returned that string — it returns
         * `SAFETY_BLOCKED` — so the content-filter notification was DEAD in every portal build,
         * and a refused prompt fell through to the `contentUrl` branch and did nothing visible. It
         * also required a non-empty `errorMessage`, which the provider deliberately never sets
         * ("return a stable machine code the client maps to a user message"), so the branch could
         * not have fired even with the right code. Both conditions are now the code alone, and any
         * OTHER failure code gets the generic error rather than silence.
         */
        const answerCode = generateAiImageData?.data?.code ?? '';
        if (answerCode === 'SAFETY_BLOCKED') {
          notification.error({
            message: t('shared.exposure.imageGenerationError'),
            description: t('shared.exposure.contentFilter'),
            duration: 20,
          });
          return;
        }
        if (answerCode && !generateAiImageData?.data?.contentUrl) {
          notification.error({
            message: t('shared.exposure.imageGenerationError'),
            duration: 10,
          });
          return;
        }

        if (
          generateAiImageData?.data?.contentUrl !== null &&
          lastGeneratedImageUrlRef.current ===
            generateAiImageData?.data?.contentUrl
        ) {
          return;
        }

        setImageLoading(true);

        /**
         * THE GENERATED IMAGE IS ALREADY IN OUR STORAGE — USE IT WHERE IT LIES (founder approved
         * 2026-09-10, after portal image generation failed in production while mobile worked).
         *
         * This used to `fetch()` the returned URL, take the blob, and `uploadBytes` it back into the
         * SAME bucket under `imagePrefix`. Two things were wrong with that:
         *
         *   • It could not work in a browser at all. `contentUrl` is a Firebase Storage download URL
         *     and that endpoint returns NO `access-control-allow-origin` header (verified against the
         *     bucket, 2026-09-10 — it has no CORS configuration), so the cross-origin `fetch` is
         *     blocked before the blob arrives. Mobile was unaffected because React Native's `fetch`
         *     does not enforce the same-origin policy, which is exactly why the founder saw images
         *     generate on his phone and fail on the portal while the SERVER succeeded in both cases
         *     (four requests in `usage_log`, four answers in `event_log`, each ~10 s).
         *   • Even where it worked it moved 1.6 MB down and 1.6 MB back up to relocate a file that
         *     was already ours, with the therapist waiting on both legs.
         *
         * The upload affordance beside this one is untouched and never needed the round-trip: antd's
         * `Upload` hands `uploadBytes` a local `File` straight from disk, so it never crosses an
         * origin. This was the portal's ONLY `fetch()` of a storage object.
         */
        const imageUrl = generateAiImageData?.data?.contentUrl;
        if (imageUrl) {
          try {
            setFiles([
              ...files,
              {
                uid: String(files.length + 1),
                name: `photo-${files.length + 1}.png`,
                status: 'done',
                // @ts-ignore
                url: imageUrl,
              },
            ]);

            form!.setFields([
              {
                name: 'imageUrls',
                touched: true,
                value: [...(form.getFieldValue('imageUrls') || []), imageUrl],
              },
            ]);

            lastGeneratedImageUrlRef.current = imageUrl;

            // NEVER A BARE `setTimeout` AROUND A REJECTING MUTATION. `updateExposureStimulus`
            // posts with `errorNotification: false` and REJECTS, so an uncaught call is a
            // refusal nobody sees — the shape that swallowed a whole exposition run here.
            setTimeout(() => {
              void updateExposureStimulus().catch(reportFailure);
            }, 50);

            message.success(t('common.uploadSuccess'));
          } catch (e) {
            reportFailure(e);

            setImageLoading(false);
          }
        }

        setImageLoading(false);
      }
    };
    processImage();
  }, [JSON.stringify(generateAiImageData?.data)]);

  const handlePreview = async (file: any) => {
    if (file.url) {
      setPreviewImage(file.url);
    } else if (file.response) {
      setPreviewImage(file.response.url);
    }
    startExposure();
  };

  const updateExposureStimulus = useCallback(
    async (
      passedValues?: typeof situationInitialValues,
      opts?: { omitExpositions?: boolean },
    ) => {
      const values = passedValues || form.getFieldsValue();
      const { _id, createdAt, updatedAt, __typename, userId, ...rest } =
        deepOmit(values, ['__typename']);

      /**
       * THE SAVE STOPS CARRYING `expositions` WHEN THE LOG OWNS THEM (E20 WP2 / T115 (b)). The
       * shipped form keeps the run history in a hidden field and posts it back with every save; for
       * a log-owned rung the decider compares that echo against the log and refuses one that would
       * CHANGE it (`EXPOSITION_EDIT_NOT_PORTED`). Sending nothing is not a workaround for the
       * guard — it is what an E-era client legitimately says: the runs are not this command's to
       * write any more. For a legacy rung the field still travels exactly as it always has.
       *
       * `omitExpositions` is the SAME statement made by a caller that has just learnt the era
       * changed under it: `esRunner` is React state derived from queries that have not answered
       * again yet, so the recovery path below says so explicitly rather than waiting a render.
       */
      const { expositions, ...withoutRuns } = rest as Record<string, unknown>;
      const data = esRunner || opts?.omitExpositions ? withoutRuns : rest;

      /**
       * A `null` ANSWER IS A REFUSAL, NEVER A SAVE (review 2026-09-05, D6). The shipped resolver
       * answers a silent null for a permission miss and for a ladder/client mismatch — the legacy
       * contract, deliberately kept — and refine's `onSuccess` fires on `data: null` exactly as it
       * does on a row. So this promise used to resolve, the toast said "Situation saved", and
       * `dirty` was cleared over a write that never happened: a therapist whose grant was revoked
       * mid-session was told their clinical note was safe. Rejecting instead sends it through
       * `reportFailure`, which keeps the work on screen, keeps Save offered, and files it.
       */
      await new Promise<void>((resolve, reject) => {
        upsertExposureStimulus(
          {
            url: '',
            method: 'post',
            values: {},
            meta: {
              operation: 'upsertExposureStimulus',
              fields: ['_id'],
              variables: {
                input: {
                  name: 'input',
                  type: 'ExposureStimulusInput',
                  value: {
                    data,
                    where: {
                      clientId,
                      exposureId,
                      _id: exposureStimulusId || null,
                    },
                  },
                },
              },
            },
            errorNotification: false,
          },
          {
            onSuccess: (res) =>
              isRefusedAnswer(res?.data)
                ? reject(refusedAnswerError('upsertExposureStimulus'))
                : resolve(),
            onError: (e) => reject(e),
          },
        );
      });
      message.success(t('shared.exposure.situationOrStimulusSaveSuccess'));

      setTimeout(() => {
        refetchStimulusExposure();
      }, 300);
    },
    [clientId, exposureId, exposureStimulusId, esRunner, form, t],
  );

  /**
   * EXPLICIT SAVE (T99 (f)). This editor autosaved `onValuesChange` on a 700 ms debounce — see the
   * long note on the same change in `create.tsx` for why that had to go and why it went for BOTH
   * eras. The image upload/removal and the exposition drawer keep writing when they act, because
   * each of those IS a deliberate act with its own button; what stopped writing is TYPING.
   */
  const [dirty, setDirty] = useState(false);

  const onValuesChange = useCallback(() => {
    setDirty(true);
    setWarnWhen(true);
  }, [setWarnWhen]);

  const onSave = useCallback(async () => {
    try {
      await updateExposureStimulus();
    } catch (e) {
      reportFailure(e);
      return;
    }
    setDirty(false);
    setWarnWhen(false);
  }, [updateExposureStimulus, setWarnWhen, reportFailure]);

  const expositionsStats = useMemo(() => {
    const data = flatten(
      (form.getFieldValue('expositions') || [])
        // @ts-ignore
        .filter((e) => e.suds.length > 0)
        // @ts-ignore
        .map((ex, exIdx, exArr) => {
          // @ts-ignore
          return ex.suds.map((e, idx, arr) => {
            return {
              type: t('shared.exposure.title') + ' ' + (exIdx + 1),
              count: e.sud,
              date:
                idx === 0
                  ? 0
                  : differenceInSeconds(
                      parseISO(e.reactedAt),
                      parseISO(arr[0].reactedAt),
                    ),
            };
          });
        }),
    )
      .sort((a, b) => {
        // @ts-ignore
        return a.date - b.date;
      })
      .map((e, idx) => {
        return {
          // @ts-ignore
          ...e,
          date: `${format(
            addSeconds(
              set(new Date(), {
                hours: 0,
                minutes: 0,
                seconds: 0,
              }),
              // @ts-ignore
              e.date,
            ),
            currentLocale?.startsWith('ru') ? 'H:mm:ss' : 'h:mm:ss aaa',
          )}`,
        };
      });

    return {
      smooth: true,
      xField: 'date',
      yField: 'count',
      point: {
        size: 5,
        shape: 'diamond',
      },
      seriesField: 'type',
      lineStyle: {
        lineWidth: 3,
      },
      data,
      legend: {
        layout: 'horizontal',
      },
      xAxis: {
        title: {
          text: t('shared.exposure.expositionDuration'),
        },
      },

      yAxis: {
        title: {
          text: t('shared.exposure.sud'),
        },
      },
    };
  }, [JSON.stringify(form.getFieldValue('expositions'))]);

  // ── the exposition runner ──────────────────────────────────────────────────────────────────────

  const esCommands = useExpositionEsCommands({
    subject: clientId,
    exposureStimulusId,
  });

  /** After every ES beat: re-render the run from the ROW the mutation answered with. */
  const adoptEsResult = useCallback(
    (result: { expositionId: string; exposureStimulus: unknown } | null) => {
      if (!result) return;
      setEsExpositionId(result.expositionId);
      const runs = runsOf(result.exposureStimulus as ExposureStimulus);
      const run =
        runs.find((r) => r?.expositionId === result.expositionId) ??
        runs[runs.length - 1] ??
        null;
      setEsRun(run);
      // the situation form's hidden `expositions` field feeds the graph and the history table, so
      // it follows server truth too — it is a projection now, never a draft
      form.setFieldValue('expositions', runs);
    },
    [form],
  );

  /** A coexistence refusal means this page's era is stale (T112) — re-ask it, then report. */
  const reportRunFailure = useCallback(
    (error: unknown) => {
      if (isEraMismatch(error)) void refetchEra();
      reportFailure(error);
    },
    [refetchEra, reportFailure],
  );

  // ── the CORRECTION of one recorded point (founder ruling `amend-a-recorded-reaction`) ──────────

  /**
   * WHICH point the therapist is correcting, and what they have typed so far. `null` = the
   * correction panel is closed, which is its normal state: correcting is a deliberate second act
   * on a reading that came out wrong, not something a stray drag can do.
   *
   * The panel edits a COPY. Nothing on screen changes until the server has recorded the correction
   * and answered with the row — the same rule every other beat of this runner follows, and the one
   * that keeps the drawer showing the log rather than a hope.
   */
  const [amending, setAmending] = useState<{
    reactionId: string;
    ordinal: number;
    sud: number;
    reactedAt: string;
    /** minutes + seconds INTO THE RUN — what the panel edits; `reactedAt` is derived on save. */
    duration: { minutes: string; seconds: string };
  } | null>(null);

  /** A run as the LEGACY drawer builds it — the record it used to post inside `expositions[]`. */
  type DrawerRun = {
    createdAt?: string | null;
    anticipatedSud?: number | null;
    reactionNotes?: string | null;
    suds?: ({ sud?: number | null; reactedAt?: string | null } | null)[] | null;
  };

  /**
   * THE HANDLE A PARTIAL RE-ISSUE ALREADY MINTED (review 2026-09-05, D2 — R6).
   *
   * A re-issue is Start + N reactions + Finish, and a transport failure can land BETWEEN them. The
   * catch reported and left the drawer open with `legacyRunIsNew` still true, so the therapist's
   * second Save ran `startExposition` AGAIN: two runs for one exposure — one unfinished with a
   * subset of the points, one complete — and `D-no-discard` stands, so nothing can remove the
   * stray. The ref is what makes the retry a RESUME instead: the run the log already holds is
   * named, Start is skipped, and the remaining beats continue against it.
   *
   * It is a ref rather than state deliberately: `saveLegacyRun` awaits its way through the retry
   * inside one callback, and a state update would not be visible to the pass that set it.
   *
   * `recorded` is how far the log got, taken from the SERVER's own answer each time (every command
   * replies with the row the inline projection wrote, plan 04 D3). The ONE case it cannot see is a
   * reply lost after its write landed; the retry then re-sends that point and the run carries one
   * duplicate reading — visible, correctable now that a point can be corrected, and a far smaller
   * thing than the second RUN this replaces, which `D-no-discard` gives no way to remove.
   *
   * THE ROW IS STILL NOT REFETCHED HERE (T112). Asking the server directly would be a better
   * count, and it is exactly what must not happen mid-recovery: the rung's identity arriving would
   * flip `esRunner`, swap the drawer to the E-era twin and unmount the legacy form holding the work
   * this recovery exists to save.
   */
  const reissuedHandleRef = useRef<{
    handle: string;
    recorded: number;
  } | null>(null);

  /**
   * RE-ISSUE A LEGACY-SHAPED RUN THROUGH THE E-ERA COMMANDS (T112 / the P010 recipe).
   *
   * The legacy drawer builds a whole `{createdAt, anticipatedSud, suds[], reactionNotes}` record
   * and posts it on Save; the E era says the same thing as a sequence of facts. The translation is
   * exact for a NEW run and needs no vocabulary the model does not have:
   *
   *   Start   → `StartExposition {anticipatedSud}`   (mints the handle)
   *   each ✓  → `RecordExpositionReaction {sud, reactedAt}`  — the point's OWN clock is preserved,
   *             which is why the curve survives the crossing rather than being restamped
   *   Save    → `FinishExposition {duration, notes}`
   *
   * The one thing that cannot cross is the run's START INSTANT: `StartExposition` takes no
   * `startedAt` (the server clock is the event time), so a re-issued run's `createdAt` is the
   * moment of recovery, not the moment the stopwatch was pressed. Named rather than smuggled: the
   * SUD points keep their real times, so the shape of the habituation curve is unchanged.
   *
   * A RETRY RESUMES; IT NEVER STARTS A SECOND RUN (D2). Two things make that exact:
   *   • the minted handle survives the failure in `reissuedHandleRef`, so Start is skipped;
   *   • the points already recorded are read off the SERVER's answer, not counted locally — every
   *     command replies with the row the inline projection just wrote (read-your-writes, plan 04
   *     D3), so "how much of this run is already in the log" is a question only the server gets to
   *     answer. Counting locally would double a point whose reply was the message that got lost.
   * `RecordExpositionReaction` has no de-duplication rule by design (two identical ratings a second
   * apart are ordinary data), so skipping what the log already holds is the caller's job, and this
   * is the caller.
   */
  const reissueLegacyRunAsEs = useCallback(
    async (run: DrawerRun): Promise<boolean> => {
      const drafted = (run.suds ?? []).filter(
        (p) => typeof p?.sud === 'number',
      );

      /**
       * How many of THIS run's points the server says it holds, read off the row a command
       * answered with. Never a local tally: each command replies with the row the inline
       * projection wrote in the same transaction, so this is the log's own count.
       */
      const recordedIn = (
        result: ExpositionResult | null,
        handle: string,
      ): number => {
        const row = runsOf(result?.exposureStimulus as ExposureStimulus).find(
          (r) => r?.expositionId === handle,
        );
        return (row?.suds ?? []).length;
      };

      let handle = reissuedHandleRef.current?.handle ?? null;
      let already = reissuedHandleRef.current?.recorded ?? 0;
      let latest: ExpositionResult | null = null;

      if (!handle) {
        const started = await esCommands.startExposition(
          typeof run.anticipatedSud === 'number' ? run.anticipatedSud : null,
        );
        handle = started?.expositionId ?? null;
        // a null answer is the E07 grant gate saying no, silently (board GWT) — never a success
        if (!handle) return false;
        latest = started;
        already = recordedIn(started, handle);
        reissuedHandleRef.current = { handle, recorded: already };
      }

      for (let i = already; i < drafted.length; i += 1) {
        const point = drafted[i]!;
        latest = await esCommands.recordReaction(
          handle,
          point.sud as number,
          point.reactedAt
            ? new Date(String(point.reactedAt)).toISOString()
            : undefined,
        );
        // the mark advances only on an ANSWER, so a failure here leaves the ref naming exactly what
        // the log holds and the retry picks up from there
        reissuedHandleRef.current = {
          handle,
          recorded: Math.max(i + 1, recordedIn(latest, handle)),
        };
      }

      latest = await esCommands.finishExposition(
        handle,
        Math.max(0, Math.round(time / 1000)),
        run.reactionNotes ?? null,
      );
      adoptEsResult(latest);
      // the run is whole in the log — the next legacy save on this drawer is a fresh run again
      reissuedHandleRef.current = null;
      return true;
    },
    [esCommands, adoptEsResult, time],
  );

  /**
   * THE DRAWER'S SAVE, IN THE LEGACY ERA — and the one place a therapist's session notes used to
   * disappear without a word.
   *
   * What it did: closed the drawer, then fired `updateExposureStimulus` from a bare `setTimeout`.
   * The mutation posts with `errorNotification: false` and REJECTS, so with no `.catch` the
   * rejection was unhandled and invisible — and when a flip had landed mid-run the server was
   * refusing it every time (`EXPOSITION_EDIT_NOT_PORTED`: the log had taken ownership of the run
   * history under the open drawer). The therapist saw a drawer close and nothing else. Their run —
   * start, SUD curve, notes — was gone.
   *
   * What it does now: the write is AWAITED, and the drawer does not close until the work is safe.
   * On an era signal it re-reads the era and RE-ISSUES the run through the ES commands, so the run
   * actually lands; where the model genuinely cannot express the write (an edit of a run the log
   * already holds — no `AmendExpositionReaction` exists) it keeps the drawer open with every field
   * intact and says exactly what to do. Silence is not one of the outcomes.
   */
  const saveLegacyRun = useCallback(async () => {
    setIsStopwatchRunning(false);
    const run = exposureForm.getFieldsValue() as DrawerRun;
    const drafted = [
      ...((form.getFieldValue('expositions') || []) as unknown[]),
    ];
    drafted[expositionIdx] = { ...run };
    form.setFieldValue('expositions', drafted);

    const closeDrawer = () => {
      setOpenDrawer(false);
      if (previewImage) setPreviewImage('');
    };

    try {
      await updateExposureStimulus();
      closeDrawer();
      return;
    } catch (error) {
      if (!isEraSignal(error)) {
        // an ordinary failure: the drawer stays open, the run stays on screen, Save stays offered
        reportFailure(error);
        return;
      }
      // the era this page loaded with is stale — re-ask it (T112). The ROW is deliberately NOT
      // refetched here: identities arriving mid-draft would swap the drawer to the E-era twin and
      // lose exactly the work this recovery exists to save.
      void refetchEra();

      if (!legacyRunIsNew) {
        message.error(t('pages.exposure.runEditNotPorted'));
        return;
      }

      try {
        if (!(await reissueLegacyRunAsEs(run))) {
          message.error(t('pages.exposure.runEditNotPorted'));
          return;
        }
      } catch (retryError) {
        reportFailure(retryError, 'pages.exposure.runReissueFailed');
        return;
      }

      // the RUN is in the log. The rung's own fields still have to land, and this client's saves
      // may not carry the run echo any more — hence the explicit `omitExpositions`.
      try {
        await updateExposureStimulus(undefined, { omitExpositions: true });
      } catch (fieldError) {
        // the run is safe; the situation fields are not — say so and leave Save offered
        reportFailure(fieldError);
      }
      message.success(t('pages.exposure.runRecordedAfterFlip'));
      closeDrawer();
      /**
       * RE-READ BOTH HALVES OF THE PAIR, now that the work is safe (T112: refresh the signal AFTER
       * the write). The run could only be re-issued because the rung is log-owned, and
       * `runnerUsesEs` needs the LADDER's identity as well as the rung's — this used to re-read the
       * rung alone, so `exposureData` kept its pre-flip shape, `data-era` stayed "legacy", the
       * "still being moved" notice appeared on a rung that had moved, and the next Start opened the
       * legacy drawer for a run the server would refuse and re-issue all over again, until reload.
       * Found in review 2026-09-05; pinned by `E21-exposure-era-flip-midsession.spec.ts` test 3.
       */
      setTimeout(() => {
        void refetchStimulusExposure();
        void refetchExposure();
      }, 300);
    }
  }, [
    exposureForm,
    form,
    expositionIdx,
    previewImage,
    updateExposureStimulus,
    reportFailure,
    refetchEra,
    legacyRunIsNew,
    reissueLegacyRunAsEs,
    refetchStimulusExposure,
    refetchExposure,
    t,
  ]);

  /**
   * OPEN the correction panel on one recorded point. The handle comes off the point itself
   * (`reactionHandle`): the server minted it when the reaction was recorded, and a point
   * conversion carried has none of its own and is addressed by its position — the portal never
   * invents a handle.
   */
  const startAmend = useCallback(
    (point: RunPoint, ordinal: number) => {
      if (!esExpositionId) return;
      const reactedAt = point?.reactedAt
        ? new Date(String(point.reactedAt)).toISOString()
        : new Date().toISOString();
      setAmending({
        reactionId: reactionHandle(point, esExpositionId, ordinal),
        ordinal,
        sud: typeof point?.sud === 'number' ? point.sud : 50,
        reactedAt,
        // the two boxes the person actually edits — a DURATION, as on the app
        duration: toDurationFields(elapsedSecondsOfPoint(esRun, reactedAt)),
      });
    },
    [elapsedSecondsOfPoint, esExpositionId, esRun],
  );

  /**
   * RECORD the correction. It is a NEW FACT, never an edit: `ExpositionReactionRecorded` stays in
   * the log and `ExpositionReactionAmended` supersedes it for display, which is what lets the
   * curve be right AND the audit trail survive (founder ruling 2026-09-04).
   *
   * The panel closes only after the server has answered, and the drawer re-renders from the row
   * that answer carried — a correction the server refused must leave the reading on screen exactly
   * as it was.
   */
  const saveAmend = useCallback(async () => {
    if (!amending || !esExpositionId) return;
    try {
      /**
       * The panel edits a DURATION; the command still carries the instant, so the elapsed seconds
       * are added back onto the run's own start. An unreadable field (the sanitizer makes it
       * unreachable) leaves the reading's time exactly where it was.
       */
      const seconds = fromDurationFields(amending.duration);
      const anchor = esRun?.createdAt || esRun?.suds?.[0]?.reactedAt;
      const reactedAt =
        seconds !== null && anchor
          ? addSeconds(parseISO(String(anchor)), seconds).toISOString()
          : amending.reactedAt;
      const res = await esCommands.amendReaction(
        esExpositionId,
        amending.reactionId,
        amending.sud,
        reactedAt,
      );
      if (!res) {
        // a null answer is the E07 grant gate saying no, silently — never a success (D6)
        message.error(t('pages.exposure.amendFailed'));
        return;
      }
      adoptEsResult(res);
      setAmending(null);
      message.success(t('pages.exposure.amendSaved'));
    } catch (e) {
      reportRunFailure(e);
    }
  }, [
    amending,
    esExpositionId,
    esCommands,
    esRun,
    adoptEsResult,
    reportRunFailure,
    t,
  ]);

  const startExposure = () => {
    if (esRunner) {
      // NOTHING IS CREATED YET. In the E era the run begins when the timer does
      // (`ExpositionStarted` carries the anticipated SUD), so opening the drawer opens an empty
      // form — a drawer the therapist closes again has recorded nothing, which is the same promise
      // the legacy drawer made by splicing an empty record back out.
      setEsExpositionId(null);
      setEsRun(null);
      setEsResuming(false);
      setAmending(null);
      setCurrentSud(50);
      exposureForm.resetFields();
      exposureForm.setFieldsValue({ ...emptyExposition });
      setOpenDrawer(true);
      setTime(0);
      return;
    }
    const oldValues = form.getFieldValue('expositions') || [];
    form.setFieldValue('expositions', [...oldValues, { ...emptyExposition }]);
    setExpositionIdx(oldValues.length);
    // a run the row does not hold yet — re-issuable through the E-era commands if a flip lands
    setLegacyRunIsNew(true);
    // a NEW run has re-issued nothing; a handle left over from an earlier drawer would attach this
    // run's points to that one
    reissuedHandleRef.current = null;
    setOpenDrawer(true);
    const neededExpositon =
      form.getFieldValue('expositions')?.[oldValues.length];
    exposureForm.setFieldsValue({
      ...neededExpositon,
    });
    setTime(0);
  };

  /**
   * RESUME a recorded run (E era). `FinishExposition` explicitly records a later, different finish
   * as a REVISION — its own docstring: "the runner lets a person re-open a run, add points and stop
   * again" — so the supported half of the shipped Edit button is kept and only the unsupported half
   * is gone: amending a recorded point's level or its clock, which no E20 slice records.
   */
  const resumeExposure = (run: Exposition, ordinal: number) => {
    // THE DERIVED HANDLE IS BUILT FROM THE ROW'S ES IDENTITY, NOT ITS LEGACY `_id`. A run the
    // CONVERSION carried has no `expositionId` of its own and is addressed by `<esId>#<ordinal>`
    // (T115 (a), `legacyExpositionId` in the domain). Passing `exposureStimuli/<key>` here built a
    // handle the fold can never match, so Resume on a carried run was a button the server refuses.
    setEsExpositionId(
      expositionHandle(
        run,
        exposureStimulusData?.data?.exposureStimulusId || exposureStimulusId,
        ordinal,
      ),
    );
    setEsRun(run);
    setEsResuming(true);
    setAmending(null);
    setCurrentSud(endSud(run) ?? run?.anticipatedSud ?? 50);
    exposureForm.resetFields();
    exposureForm.setFieldsValue({
      anticipatedSud: run?.anticipatedSud ?? 50,
      reactionNotes: run?.reactionNotes ?? '',
      suds: [],
      createdAt: run?.createdAt ?? undefined,
      // D-expectancies-2026-10-07 — an older run has none ("not asked"), and stays unanswered
      expectedOutcome: run?.expectedOutcome ?? undefined,
      expectedLikelihood: run?.expectedLikelihood ?? undefined,
      outcomeOccurred: run?.outcomeOccurred ?? undefined,
      likelihoodAfter: run?.likelihoodAfter ?? undefined,
      learned: run?.learned ?? undefined,
    } as Parameters<typeof exposureForm.setFieldsValue>[0]);
    setOpenDrawer(true);
    // THE CLOCK CONTINUES, it does not restart. Finishing a reopened run records a NEW duration,
    // so a stopwatch reset to zero would write "this run lasted 0 seconds" over a run that visibly
    // lasted a minute — found by driving the real UI, not by reading it.
    setTime(runDurationSeconds(run) * 1000);
    message.info(t('pages.exposure.runResumed'));
  };

  const onCloseDrawer = () => {
    setIsStopwatchRunning(false);
    setOpenDrawer(false);
    // whatever a re-issue managed to land stays in the log (D-no-discard); this drawer is simply
    // no longer the thing that would continue it
    reissuedHandleRef.current = null;
    if (esRunner) {
      // an E-era run is the log's from the moment Start was pressed; closing the drawer abandons
      // nothing and deletes nothing (discarding a run has no modelled command — founder ruling
      // `D-no-discard`: a started exposition is never thrown away, it is RESUMED)
      setEsExpositionId(null);
      setEsRun(null);
      setEsResuming(false);
      setAmending(null);
      if (previewImage) setPreviewImage('');
      return;
    }
    if (!exposureForm.getFieldValue('suds').length) {
      const newExpositions = [...form.getFieldValue('expositions')];
      newExpositions.splice(expositionIdx, 1);
      form.setFieldValue('expositions', newExpositions);
    }

    if (previewImage) {
      setPreviewImage('');
    }
  };
  useEffect(() => {
    if (!!upsertExposureStimulusData?.data && exposureStimulusId) {
      therapistActivitySend({
        type: 'UPDATED',
        resource: exposureStimulusId,
      });
    }
  }, [upsertExposureStimulusData]);

  return (
    <Form<typeof situationInitialValues>
      form={form}
      onValuesChange={onValuesChange}
      onFinish={(values) => {}}
      labelCol={{ span: 7 }}
      wrapperCol={{ span: 16 }}
      labelWrap
      data-testid="cc-exposure-situation-form"
      data-era={esRunner ? 'es' : 'legacy'}
    >
      <List
        title={t('shared.exposure.situationOrStimulus')}
        headerButtons={
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 10,
            }}
          >
            {/* T99 (f): the editor no longer writes while you type — saving is a deliberate act */}
            <SaveButton
              onClick={onSave}
              disabled={!dirty}
              loading={upsertExposureStimulusLoading}
              data-testid="cc-exposure-situation--save"
            >
              {t('pages.exposure.saveSituation')}
            </SaveButton>
          </div>
        }
        breadcrumb={
          <Breadcrumb>
            <Breadcrumb.Item key={'exposure'}>
              <Link
                to={`/tools/exposures/create?exposureId=${exposureId}`}
                className={styles['exposure--back-row']}
              >
                <ArrowLeftOutlined
                  style={{
                    marginRight: 10,
                    fontSize: 16,
                  }}
                />

                {t('common.back')}
              </Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
      >
        {unconvertedRung && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            data-testid="cc-exposure-situation-unconverted"
            message={t('pages.exposure.unconvertedRung')}
          />
        )}

        <Form.Item
          name={'title'}
          label={t('shared.exposure.situationOrStimulusTitle')}
        >
          <Input.TextArea
            data-testid="cc-create-exposure-situation-title"
            placeholder={t(
              'shared.exposure.situationOrStimulusTitlePlaceholder',
            )}
            autoSize={{ minRows: 1, maxRows: 4 }}
          />
        </Form.Item>
        <Form.Item
          name={'description'}
          label={t('shared.exposure.situationOrStimulusDescription')}
        >
          <Input.TextArea
            data-testid="cc-create-exposure-situation-description"
            placeholder={t(
              'shared.exposure.situationOrStimulusDescriptionPlaceholder',
            )}
            autoSize={{ minRows: 2, maxRows: 6 }}
          />
        </Form.Item>

        <Row>
          <Col
            span={(innerWidth || 0) > 800 ? 8 : 24}
            offset={(innerWidth || 0) > 800 ? 5 : 0}
          >
            <Form.Item
              label={t('shared.exposure.avoiding')}
              tooltip={t('shared.exposure.doYouAvoid')}
              name={'avoidance'}
              style={{ margin: 0, minWidth: 120 }}
            >
              <Select
                className={styles['exposure--client-selector']}
                placeholder={t('shared.exposure.avoiding')}
                optionFilterProp="children"
                dropdownStyle={{ minWidth: 240 }}
                data-testid="cc-create-exposure-situation-avoidance"
              >
                {memoizedAvoidanceOptions}
              </Select>
            </Form.Item>
          </Col>
          <Col span={(innerWidth || 0) > 800 ? 8 : 24}>
            <Form.Item
              name={'sud'}
              tooltip={t('shared.exposure.sud')}
              label={t('shared.exposure.sudTip')}
              labelCol={{ span: (innerWidth || 0) > 800 ? 20 : 7 }}
              wrapperCol={{ span: 4 }}
              style={{ marginTop: (innerWidth || 0) > 800 ? 0 : 20 }}
            >
              <InputNumber
                min={0}
                max={100}
                data-testid="cc-create-exposure-situation-sud-input"
              />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name={'expositions'}>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          name={'worstThingThatCouldHappen'}
          label={t('shared.exposure.worstThingThatCouldHappen')}
        >
          <Input.TextArea
            placeholder={t('shared.exposure.worstThingThatCouldHappen')}
            autoSize={{ minRows: 2, maxRows: 6 }}
            data-testid="cc-create-exposure-situation-worst-thing"
          />
        </Form.Item>
        <Form.Item
          name={'imagePrompt'}
          tooltip={t('shared.exposure.imagePromptTip')}
          label={t('shared.exposure.imagePrompt')}
        >
          <Input.TextArea
            placeholder={t('shared.exposure.imagePromptPlaceholder')}
            autoSize={{ minRows: 2, maxRows: 6 }}
            data-testid="cc-create-exposure-situation-image-prompt"
          />
        </Form.Item>

        <Form.Item
          name="imageUrls"
          label={t('shared.exposure.situationOrStimuliImages')}
        >
          <div className={styles['exposure--generate-row']}>
            <Button
              data-testid="cc-create-exposure-situation-generate-image"
              icon={<BulbOutlined />}
              onClick={generateNewImage}
              loading={imageLoading || generateAiImageLoading}
              disabled={
                files.length > 4 ||
                imageLoading ||
                !imagePrompt ||
                generateAiImageLoading
              }
            >
              {t('shared.exposure.generateImageUsingAI')}
            </Button>
            <div className={styles['exposure--generate-row--or']}>
              {t('common.or')}
            </div>
            <ImgCrop rotationSlider cropShape="rect" zoomSlider>
              <Upload
                accept="image/*"
                className={styles['exposure-upload-card']}
                beforeUpload={(file) => {
                  const isImage = file.type.indexOf('image/') === 0;
                  if (!isImage) {
                    message.error(t('profile.onlyImage'));
                  }

                  // You can remove this validation if you want
                  const isLt5M = file.size / 1024 / 1024 < 5;
                  if (!isLt5M) {
                    message.error(t('profile.sizeLimit'));
                  }
                  return isImage && isLt5M;
                }}
                customRequest={({ onError, onSuccess, file }) => {
                  const upload = async () => {
                    const storage = getStorage();
                    const imageRef = ref(
                      storage,
                      imagePrefix + generateToken(),
                    );

                    try {
                      const image = await uploadBytes(imageRef, file as RcFile);
                      // @ts-ignore
                      onSuccess(null, image);

                      const imageUrl = await getDownloadURL(imageRef);

                      form!.setFields([
                        {
                          name: 'imageUrls',
                          touched: true,
                          value: [
                            ...(form.getFieldValue('imageUrls') || []),
                            imageUrl,
                          ],
                        },
                      ]);

                      // a refusal here must be seen, not swallowed (see the note above)
                      setTimeout(() => {
                        void updateExposureStimulus().catch(reportFailure);
                      }, 50);
                      message.success(t('profile.uploadSuccess'));
                    } catch (e) {
                      reportFailure(e);
                    }
                  };
                  upload();
                }}
                listType="picture-card"
                fileList={files}
                onChange={({ file, fileList }) => {
                  if (file.status === 'done') {
                    const filteredDone = fileList.filter(
                      (f) => f.status === 'done',
                    );
                    setFiles([...files, ...filteredDone]);
                  }
                  if (file.status === 'removed') {
                    const filteredRemoved = files.filter(
                      (f) => f.uid !== file.uid,
                    );
                    setFiles(
                      filteredRemoved.map((f, idx) => {
                        return {
                          ...f,
                          uid: String(idx + 1),
                          name: `photo-${idx + 1}.png`,
                        };
                      }),
                    );
                  }
                }}
                onRemove={async (file) => {
                  const index = files.indexOf(file);
                  const newFileList = files.slice();
                  newFileList.splice(index, 1);
                  const newImageUrls = form.getFieldValue('imageUrls');
                  newImageUrls?.splice(index, 1);

                  try {
                    setFiles(
                      newFileList.map((f, idx) => {
                        return {
                          ...f,
                          uid: String(idx + 1),
                          name: `photo-${idx + 1}.png`,
                        };
                      }),
                    );

                    form.setFields([
                      {
                        name: 'imageUrls',
                        touched: true,
                        value: newImageUrls,
                      },
                    ]);

                    /**
                     * DELETE WHAT THE URL ACTUALLY POINTS AT, not what we assume the prefix was.
                     *
                     * This used to take the filename off the end of the URL and re-attach
                     * `imagePrefix`, which held only while every image had been uploaded by THIS
                     * screen. Since the AI path stopped round-tripping its images through storage
                     * (2026-09-10) a generated image lives under `ai-generated/`, so the reassembled
                     * path pointed at nothing: the delete threw `object-not-found`, the therapist got
                     * an error toast for a removal that had visibly worked, and the real object was
                     * orphaned. A Firebase download URL already carries the full object path between
                     * `/o/` and the query, percent-encoded — so read it rather than rebuild it, which
                     * is also correct for an uploaded image whose prefix has since changed.
                     */
                    const objectPath = decodeURIComponent(
                      (file.url || '').split('/o/')[1]?.split('?')[0] ?? '',
                    );

                    const storage = getStorage();
                    const imageRef = ref(storage, objectPath);

                    const res = objectPath
                      ? await deleteObject(imageRef)
                      : undefined;

                    // a refusal here must be seen, not swallowed (see the note above)
                    setTimeout(() => {
                      void updateExposureStimulus().catch(reportFailure);
                    }, 50);
                  } catch (e) {
                    reportFailure(e);
                  }
                }}
                onPreview={handlePreview}
                multiple={false}
                maxCount={1}
              >
                {files.length < 5 ? (
                  <>
                    <UploadOutlined
                      className={styles['exposure-upload-card__upload-icon']}
                    />{' '}
                    {t('profile.uploadPhoto')}
                  </>
                ) : null}
              </Upload>
            </ImgCrop>
          </div>
          <Button
            className={styles['exposure-start-button']}
            type="primary"
            onClick={startExposure}
            icon={<PlusOutlined />}
            data-testid="cc-create-exposure-situation-start-exposure"
          >
            {t('shared.exposure.startExposure')}
          </Button>
          <Drawer
            title={t('shared.exposure.newExposure')}
            width={(innerWidth || 0) > 720 ? 720 : '100%'}
            open={openDrawer}
            styles={{
              body: { paddingBottom: 80 },
            }}
            autoFocus
            onClose={onCloseDrawer}
            extra={
              <Space>
                <Button
                  onClick={onCloseDrawer}
                  data-testid="cc-create-exposure-situation-cancel-exposure"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  data-testid="cc-create-exposure-situation-save-exposure"
                  disabled={
                    esRunner
                      ? !esExpositionId || esCommands.busy
                      : !exposureForm.getFieldValue('suds')?.length
                  }
                  loading={esCommands.busy || upsertExposureStimulusLoading}
                  onClick={async () => {
                    if (esRunner) {
                      // STOP + SAVE is `FinishExposition {duration, notes}` — the SUD curve is
                      // already on the record (each tap was its own fact), so this carries only
                      // what the curve does not
                      if (!esExpositionId) return;
                      setIsStopwatchRunning(false);
                      try {
                        const res = await esCommands.finishExposition(
                          esExpositionId,
                          // A RUN'S DURATION NEVER SHRINKS. `FinishExposition` records a later,
                          // different finish as a REVISION, so a resumed run that gains a point
                          // without the therapist restarting the stopwatch must keep the length it
                          // already had — otherwise the log would say a run that visibly lasted a
                          // minute lasted none. Found by resuming a run in the real UI.
                          Math.max(
                            Math.round(time / 1000),
                            runDurationSeconds(esRun),
                          ),
                          exposureForm.getFieldValue('reactionNotes') ?? null,
                          // D-expectancies-2026-10-07 — only what the person answered
                          {
                            outcomeOccurred:
                              exposureForm.getFieldValue('outcomeOccurred'),
                            likelihoodAfter:
                              exposureForm.getFieldValue('likelihoodAfter'),
                            learned: exposureForm.getFieldValue('learned'),
                          },
                        );
                        adoptEsResult(res);
                        message.success(t('pages.exposure.runSaved'));
                        setOpenDrawer(false);
                        setEsExpositionId(null);
                        setEsRun(null);
                        setEsResuming(false);
                        setAmending(null);
                        setTimeout(() => refetchStimulusExposure(), 300);
                      } catch (e) {
                        reportRunFailure(e);
                        return;
                      }
                      if (previewImage) setPreviewImage('');
                      return;
                    }
                    // THE DRAWER CLOSES ONLY WHEN THE WORK IS SAFE — see `saveLegacyRun`
                    await saveLegacyRun();
                  }}
                  type="primary"
                >
                  {esRunner
                    ? t('shared.exposure.es.finishRun')
                    : t('common.save')}
                </Button>
              </Space>
            }
          >
            <Form<typeof emptyExposition>
              form={exposureForm}
              onFinish={(values) => {}}
              labelCol={{ span: 7 }}
              wrapperCol={{ span: 16 }}
              labelWrap
            >
              <>
                {previewImage ? (
                  <Row>
                    <img
                      alt="preview"
                      style={{ width: '100%', marginBottom: 10 }}
                      src={previewImage}
                    />
                  </Row>
                ) : null}
                <Row>
                  <Col span={15} push={1}>
                    {exposureData?.data?.safetyBehaviorsToAvoid ? (
                      <Form.Item
                        label={t('shared.exposure.safetyBehaviorsToAvoid')}
                        labelCol={{ span: 12 }}
                      >
                        <Text>
                          {exposureData?.data?.safetyBehaviorsToAvoid || ''}
                        </Text>
                      </Form.Item>
                    ) : null}
                  </Col>
                </Row>
                <Row>
                  <Form.Item name={'createdAt'}>
                    <Input type="hidden" />
                  </Form.Item>

                  <Col span={15} push={1}>
                    <Form.Item
                      label={
                        t('shared.exposure.anticipated') +
                        ' ' +
                        t('shared.exposure.sud')
                      }
                      name={'anticipatedSud'}
                      labelCol={{ span: 12 }}
                    >
                      <Slider
                        data-testid="cc-create-exposure-situation-anticipated-sud-slider"
                        min={1}
                        max={100}
                        style={{
                          minWidth: 200,
                        }}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={1} offset={2}>
                    <Form.Item name={'anticipatedSud'}>
                      <InputNumber
                        min={1}
                        max={100}
                        data-testid="cc-create-exposure-situation-anticipated-sud-input"
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/*
                  D-expectancies-2026-10-07 (Q-exp-1 A: ES-ONLY) — the optional "what do you fear will
                  happen?" before Start. Shown only when the runner writes through the ES commands, so
                  a legacy subject sees today's drawer unchanged. Skippable: nothing is sent unless
                  answered, and once the run exists (Start pressed) the answers are the log's.
                */}
                {esRunner ? (
                  <>
                    <Form.Item
                      label={t('shared.exposure.expectedOutcome')}
                      name="expectedOutcome"
                    >
                      <Input.TextArea
                        data-testid="cc-exposure-expected-outcome"
                        placeholder={t(
                          'shared.exposure.expectedOutcomePlaceholder',
                        )}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                        disabled={!!esExpositionId}
                      />
                    </Form.Item>
                    <Form.Item
                      label={t('shared.exposure.expectedLikelihood')}
                      name="expectedLikelihood"
                    >
                      <ExpositionLikelihoodField
                        testId="cc-exposure-expected-likelihood"
                        disabled={!!esExpositionId}
                      />
                    </Form.Item>
                  </>
                ) : null}

                {/*
                  THE E-ERA RUN, rendered from the LOG rather than from a draft.

                  A recorded reaction is a durable fact, and since the founder's ruling of
                  2026-09-04 (`amend-a-recorded-reaction`) it can also be CORRECTED — as a
                  correction event, so the original reading stays in the log and the correction
                  supersedes it for display. That is why each point below is a button rather than
                  plain text, and why a corrected one carries its original beside it: "we can show
                  it as corrected" is the ruling's own sentence.

                  The pre-tap slider stays. Choosing the level first is no longer forced, but it is
                  still the honest gesture for a LIVE run — you record the number you mean now —
                  and correcting is a deliberate second act on a reading that came out wrong.
                */}
                {esRunner ? (
                  <>
                    <Alert
                      type="info"
                      showIcon
                      style={{ marginBottom: 16 }}
                      data-testid="cc-exposure-run-notice"
                      message={t(
                        esResuming
                          ? 'shared.exposure.es.runRecordedNotice'
                          : 'shared.exposure.es.runLiveNotice',
                      )}
                    />
                    {(esRun?.suds ?? []).length > 0 && (
                      <div
                        className={styles['exposure--sud-row']}
                        data-testid="cc-exposure-recorded-points"
                        style={{ flexWrap: 'wrap', gap: 8, marginBottom: 12 }}
                      >
                        {(esRun?.suds ?? []).map((point, idx) => {
                          const corrected = wasAmended(point);
                          /**
                           * ELAPSED SINCE THE SESSION STARTED, not a time of day (founder,
                           * 2026-09-08: *"I thought it was time since the start of the exposure
                           * setting"*). He was right, and this was ours to get wrong: the portal's
                           * old editor rendered the offset, the app renders the offset, and the
                           * amend chips introduced with E20 rendered the raw `reactedAt` instead —
                           * so one surface said 11:53:04 PM where the other said 0:00:34 for the
                           * same reading. The wall clock is still one hover away, in the tooltip.
                           */
                          const at = point?.reactedAt
                            ? formatDurationHms(
                                elapsedSecondsOfPoint(esRun, point.reactedAt),
                              )
                            : '';
                          const originalAt = corrected
                            ? formatDurationHms(
                                elapsedSecondsOfPoint(
                                  esRun,
                                  point!.amendedFrom!.reactedAt,
                                ),
                              )
                            : '';
                          const wallClock = point?.reactedAt
                            ? format(
                                parseISO(String(point.reactedAt)),
                                currentLocale?.startsWith('ru')
                                  ? 'HH:mm:ss'
                                  : 'hh:mm:ss a',
                              )
                            : '';
                          return (
                            <Tooltip
                              key={`${
                                point?.reactionId ?? point?.reactedAt
                              }-${idx}`}
                              title={
                                corrected
                                  ? `${t('pages.exposure.amendedOriginal', {
                                      sud: point!.amendedFrom!.sud,
                                      time: originalAt,
                                    })} · ${wallClock}`
                                  : `${t(
                                      'shared.exposure.es.amendPoint',
                                    )} · ${wallClock}`
                              }
                            >
                              <Tag
                                color={corrected ? 'gold' : 'blue'}
                                style={{ cursor: 'pointer' }}
                                data-testid={`cc-exposure-recorded-point-${idx}`}
                                data-amended={corrected ? 'true' : 'false'}
                                onClick={() => point && startAmend(point, idx)}
                              >
                                {point?.sud}
                                {at ? ` · ${at}` : ''}
                                {corrected
                                  ? ` · ${t('pages.exposure.amendedTag')}`
                                  : ''}
                              </Tag>
                            </Tooltip>
                          );
                        })}
                      </div>
                    )}
                    {/*
                      THE CORRECTION PANEL. It edits a COPY and writes only on its own button, so
                      dragging the slider records nothing — the same explicit-act rule the whole
                      editor follows since T99 (f). The hint says what the correction DOES with the
                      original, because "corrected" is only trustworthy if the therapist knows the
                      first reading did not disappear.
                    */}
                    {amending && (
                      <div
                        data-testid="cc-exposure-amend-panel"
                        style={{ marginBottom: 16 }}
                      >
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginBottom: 8 }}
                          message={t('pages.exposure.amendTitle')}
                          description={t('pages.exposure.amendHint')}
                        />
                        <div
                          className={styles['exposure--sud-row']}
                          style={{ flexWrap: 'wrap', gap: 8 }}
                        >
                          {/*
                            A DURATION, in two boxes — the same control the app now shows, for the
                            same reason (founder 2026-09-07 on mobile, 2026-09-08 here): a reading's
                            time is how far into the exposure it was taken, and a 12-hour AM/PM dial
                            asks a question nobody can answer about it. Seconds ≥ 60 roll over.
                          */}
                          <Input
                            addonBefore={t('shared.exposure.minutes')}
                            data-testid="cc-exposure-amend-minutes"
                            inputMode="numeric"
                            style={{ maxWidth: 150 }}
                            value={amending.duration.minutes}
                            onChange={(e) =>
                              setAmending((current) =>
                                current
                                  ? {
                                      ...current,
                                      duration: {
                                        ...current.duration,
                                        minutes: sanitizeDurationInput(
                                          e.target.value,
                                        ),
                                      },
                                    }
                                  : current,
                              )
                            }
                          />
                          <Input
                            addonBefore={t('shared.exposure.seconds')}
                            data-testid="cc-exposure-amend-seconds"
                            inputMode="numeric"
                            style={{ maxWidth: 150 }}
                            value={amending.duration.seconds}
                            onChange={(e) =>
                              setAmending((current) =>
                                current
                                  ? {
                                      ...current,
                                      duration: {
                                        ...current.duration,
                                        seconds: sanitizeDurationInput(
                                          e.target.value,
                                        ),
                                      },
                                    }
                                  : current,
                              )
                            }
                          />
                          <Slider
                            min={1}
                            max={100}
                            value={amending.sud}
                            onChange={(v) =>
                              setAmending((current) =>
                                current
                                  ? { ...current, sud: v as number }
                                  : current,
                              )
                            }
                            style={{ minWidth: 200, flex: 1 }}
                          />
                          <InputNumber
                            min={1}
                            max={100}
                            value={amending.sud}
                            onChange={(v) =>
                              setAmending((current) =>
                                current
                                  ? {
                                      ...current,
                                      sud:
                                        typeof v === 'number' ? v : current.sud,
                                    }
                                  : current,
                              )
                            }
                            data-testid="cc-exposure-amend-sud-input"
                          />
                          <Button
                            type="primary"
                            loading={esCommands.busy}
                            disabled={esCommands.busy}
                            onClick={saveAmend}
                            data-testid="cc-exposure-amend-save"
                          >
                            {t('common.save')}
                          </Button>
                          <Button
                            onClick={() => setAmending(null)}
                            data-testid="cc-exposure-amend-cancel"
                          >
                            {t('common.cancel')}
                          </Button>
                        </div>
                      </div>
                    )}
                    {/*
                      antd's `Slider` does NOT forward `data-*` to the DOM (its `InputNumber` twin
                      does), so the testid lives on a wrapper and a NUMBER input sits beside the
                      slider — the same pair the anticipated-SUD row already uses. A spec can then
                      set the level deterministically instead of dragging a rail.
                    */}
                    <Row data-testid="cc-exposure-current-sud">
                      <Col span={18}>
                        <Form.Item
                          label={t('shared.exposure.recordSudPoint')}
                          labelCol={{ span: 12 }}
                        >
                          <Slider
                            min={1}
                            max={100}
                            value={currentSud}
                            onChange={(v) => setCurrentSud(v as number)}
                            style={{ minWidth: 200 }}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={4} offset={1}>
                        <InputNumber
                          min={1}
                          max={100}
                          value={currentSud}
                          onChange={(v) =>
                            setCurrentSud(typeof v === 'number' ? v : 50)
                          }
                          data-testid="cc-exposure-current-sud-input"
                        />
                      </Col>
                    </Row>
                    <div className={styles['exposure--stopwatch']}>
                      <div>
                        <Text
                          strong
                          style={{ fontSize: '24px', marginRight: '15px' }}
                        >
                          {formatTimestampToReadableString(time)}
                        </Text>
                        <HourglassOutlined style={{ fontSize: 24 }} />
                      </div>
                      <div>
                        <Button
                          data-testid="cc-create-exposure-situation-start-stopwatch"
                          type="primary"
                          loading={esCommands.busy && !esExpositionId}
                          style={{ margin: '0 20px' }}
                          onClick={async () => {
                            // THE TIMER'S START IS `ExpositionStarted` — the first press is what
                            // creates the run in the log; every later press only pauses the clock
                            if (!esExpositionId) {
                              try {
                                const res = await esCommands.startExposition(
                                  exposureForm.getFieldValue(
                                    'anticipatedSud',
                                  ) ?? null,
                                  // D-expectancies-2026-10-07 — only what the person answered
                                  {
                                    expectedOutcome:
                                      exposureForm.getFieldValue(
                                        'expectedOutcome',
                                      ),
                                    expectedLikelihood:
                                      exposureForm.getFieldValue(
                                        'expectedLikelihood',
                                      ),
                                  },
                                );
                                adoptEsResult(res);
                              } catch (e) {
                                reportRunFailure(e);
                                return;
                              }
                            }
                            setIsStopwatchRunning(!isStopwatchRunning);
                          }}
                        >
                          {t(
                            isStopwatchRunning ? 'common.stop' : 'common.start',
                          )}
                        </Button>
                        {/*
                          A DISABLED CONTROL SAYS WHY (review 2026-09-05, D4). In the E era a run
                          begins with the timer — `ExpositionStarted` is what mints the handle a
                          reaction is recorded against — so there is nothing to attach a reading to
                          until Start has been pressed. The copy for that existed and nothing used
                          it; a button that greys out with no reason reads as broken.
                        */}
                        <Tooltip
                          title={
                            esExpositionId
                              ? undefined
                              : t('pages.exposure.runStartFirst')
                          }
                        >
                          <Button
                            data-testid="cc-create-exposure-situation-add-reaction"
                            type="primary"
                            icon={<PlusOutlined />}
                            loading={esCommands.busy && !!esExpositionId}
                            disabled={!esExpositionId || esCommands.busy}
                            onClick={async () => {
                              if (!esExpositionId) return;
                              try {
                                const res = await esCommands.recordReaction(
                                  esExpositionId,
                                  currentSud,
                                  new Date().toISOString(),
                                );
                                adoptEsResult(res);
                              } catch (e) {
                                reportRunFailure(e);
                              }
                            }}
                          >
                            {t('shared.exposure.addReaction')}
                          </Button>
                        </Tooltip>
                      </div>
                    </div>
                  </>
                ) : (
                  <Form.List name={['suds']}>
                    {(fields, { add, remove }, errors) => {
                      return (
                        <>
                          {fields.map((field, index) => {
                            const date1 =
                              exposureForm.getFieldValue('createdAt');
                            const date2 = exposureForm.getFieldValue([
                              'suds',
                              index,
                              'reactedAt',
                            ]);

                            const startDateAtMidnight = set(new Date(date1), {
                              hours: 0,
                              minutes: 0,
                              seconds: 0,
                            });
                            const secondsDifference = differenceInSeconds(
                              parseISO(date2),
                              parseISO(date1),
                            );
                            const intervalAsDate = addSeconds(
                              new Date(startDateAtMidnight),
                              secondsDifference,
                            );

                            return (
                              <div
                                key={field.key}
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  justifyContent: 'space-between',

                                  maxWidth: 900,
                                }}
                              >
                                <Divider />
                                <div className={styles['exposure--sud-row']}>
                                  <DateFnsTimePicker
                                    data-testid="cc-create-exposure-situation-reacted-at"
                                    style={{
                                      minWidth: i18n.languages[0]?.startsWith(
                                        'ru',
                                      )
                                        ? 105
                                        : 125,
                                      marginRight: 7,
                                    }}
                                    defaultValue={intervalAsDate}
                                    format={
                                      currentLocale?.startsWith('ru')
                                        ? 'HH:mm:ss'
                                        : 'hh:mm:ss a'
                                    }
                                    secondStep={5}
                                    showNow={false}
                                    onChange={(date: Date | Date[]) => {
                                      if (Array.isArray(date)) return;
                                      if (date) {
                                        const secondsDifference =
                                          differenceInSeconds(
                                            date,
                                            startDateAtMidnight,
                                          );

                                        exposureForm.setFieldValue(
                                          ['suds', index, 'reactedAt'],
                                          addSeconds(date2, secondsDifference),
                                        );
                                      }
                                    }}
                                  />

                                  <Form.Item
                                    {...field}
                                    label={t('shared.exposure.sud')}
                                    key={field.key}
                                    name={[field.key, 'sud']}
                                    labelCol={{ span: 12 }}
                                  >
                                    {/*
                                      DISTINCT FROM THE RUNG'S OWN SUD FIELD. Both this per-POINT
                                      control and the situation's own `sud` input shipped with the
                                      testid `cc-create-exposure-situation-sud-input`, so any
                                      unscoped locator resolved to TWO elements and a spec was one
                                      `.first()` away from asserting about the wrong one. The point
                                      controls now say `-point-sud-`; the rung's field keeps the
                                      original name, which is the one the legacy spec drives.
                                    */}
                                    <Slider
                                      min={1}
                                      max={100}
                                      style={{
                                        minWidth: 200,
                                      }}
                                      data-testid="cc-create-exposure-situation-point-sud-slider"
                                    />
                                  </Form.Item>

                                  <Form.Item
                                    {...field}
                                    key={field.key}
                                    name={[field.key, 'sud']}
                                    style={{
                                      marginLeft: 7,
                                    }}
                                  >
                                    <InputNumber
                                      style={{
                                        width: 50,
                                      }}
                                      min={1}
                                      max={100}
                                      data-testid="cc-create-exposure-situation-point-sud-input"
                                    />
                                  </Form.Item>
                                </div>
                              </div>
                            );
                          })}

                          <div className={styles['exposure--stopwatch']}>
                            <div>
                              <Text
                                strong
                                style={{
                                  fontSize: '24px',
                                  marginRight: '15px',
                                }}
                              >
                                {formatTimestampToReadableString(time)}
                              </Text>
                              <HourglassOutlined
                                style={{
                                  fontSize: 24,
                                }}
                              />
                            </div>
                            <div>
                              <Button
                                data-testid="cc-create-exposure-situation-start-stopwatch"
                                type="primary"
                                onClick={() => {
                                  setIsStopwatchRunning(!isStopwatchRunning);
                                  if (
                                    !exposureForm.getFieldValue('createdAt')
                                  ) {
                                    exposureForm.setFieldsValue({
                                      createdAt: new Date().toISOString(),
                                    });
                                  }
                                }}
                                style={{
                                  margin: '0 20px',
                                }}
                              >
                                {t(
                                  isStopwatchRunning
                                    ? 'common.stop'
                                    : 'common.start',
                                )}
                              </Button>
                              <Button
                                data-testid="cc-create-exposure-situation-add-reaction"
                                type="primary"
                                onClick={() => {
                                  const previusSud = exposureForm.getFieldValue(
                                    ['suds', fields.length - 1],
                                  );

                                  add({
                                    reactedAt: new Date().toISOString(),
                                    sud: previusSud?.sud || emptySud.sud,
                                  });
                                }}
                                icon={<PlusOutlined />}
                                disabled={!isStopwatchRunning}
                              >
                                {t('shared.exposure.addReaction')}
                              </Button>
                            </div>
                          </div>
                        </>
                      );
                    }}
                  </Form.List>
                )}

                <Form.Item
                  label={t('shared.exposure.reactionNotes')}
                  name={'reactionNotes'}
                >
                  <Input.TextArea
                    data-testid="cc-create-exposure-situation-reaction-notes"
                    placeholder={t('shared.exposure.reactionNotes')}
                    autoSize={{ minRows: 4, maxRows: 8 }}
                  />
                </Form.Item>

                {/* D-expectancies-2026-10-07 — the three optional answers after Stop (ES runner only,
                    and only once the run exists). Nothing is sent unless answered. */}
                {esRunner && esExpositionId ? (
                  <>
                    <Form.Item
                      label={t('shared.exposure.outcomeOccurred')}
                      name="outcomeOccurred"
                    >
                      <Radio.Group data-testid="cc-exposure-outcome-occurred">
                        <Radio
                          value="yes"
                          data-testid="cc-exposure-outcome-occurred-yes"
                        >
                          {t('shared.exposure.outcomeYes')}
                        </Radio>
                        <Radio
                          value="partly"
                          data-testid="cc-exposure-outcome-occurred-partly"
                        >
                          {t('shared.exposure.outcomePartly')}
                        </Radio>
                        <Radio
                          value="no"
                          data-testid="cc-exposure-outcome-occurred-no"
                        >
                          {t('shared.exposure.outcomeNo')}
                        </Radio>
                      </Radio.Group>
                    </Form.Item>
                    <Form.Item
                      label={t('shared.exposure.likelihoodAfter')}
                      name="likelihoodAfter"
                    >
                      <ExpositionLikelihoodField testId="cc-exposure-likelihood-after" />
                    </Form.Item>
                    <Form.Item
                      label={t('shared.exposure.learned')}
                      name="learned"
                    >
                      <Input.TextArea
                        data-testid="cc-exposure-learned"
                        placeholder={t('shared.exposure.learnedPlaceholder')}
                        autoSize={{ minRows: 2, maxRows: 6 }}
                      />
                    </Form.Item>
                  </>
                ) : null}
              </>
            </Form>
          </Drawer>
        </Form.Item>
        <>
          <Title level={5}>{t('shared.exposure.exposureGraph')}</Title>
          <Line {...(expositionsStats as unknown as LineConfig)} />
        </>
        <Table
          rowKey="createdAt"
          dataSource={form.getFieldValue('expositions') || []}
          pagination={false}
          scroll={{
            x: true,
          }}
        >
          <Table.Column<Exposure>
            key="createdAt"
            dataIndex="createdAt"
            title={t('common.createdAt')}
            /*
             * ORDER BY DATE (founder, portal testing 2026-09-10: "in the table of exposure sessions
             * in portal we lack capability to order by date"). Sorted on the ISO string's instant
             * rather than the rendered label, because the label is localised — "Sep 10" and
             * "10 сент." must sort the same way, and a string compare on the rendered text would
             * order a Russian therapist's sessions differently from an English one's.
             */
            defaultSortOrder="descend"
            sorter={(a: Exposure, b: Exposure) =>
              new Date(a?.createdAt ?? 0).getTime() -
              new Date(b?.createdAt ?? 0).getTime()
            }
            render={(createdAt) => {
              return createdAt
                ? formatDateTimeShort(createdAt, currentLocale)
                : '';
            }}
          />

          <Table.Column<typeof emptyExposition>
            key="duration"
            title={t('shared.exposure.duration')}
            render={(record) => {
              return record.suds.length > 0 && record.createdAt
                ? formatDuration(
                    intervalToDuration({
                      start: parseISO(
                        record.suds[record.suds.length - 1].reactedAt,
                      ),
                      end: parseISO(record.createdAt),
                    }),
                    {
                      format: ['hours', 'minutes', 'seconds'],
                      locale: currentLocale?.startsWith('ru') ? ru : enUS,
                    },
                  )
                : '';
            }}
          />

          <Table.Column<Exposure>
            key="anticipatedSud"
            dataIndex="anticipatedSud"
            title={
              t('shared.exposure.anticipated') + ' ' + t('shared.exposure.sud')
            }
          />

          {/*
            `Math.max()` of an empty list is -Infinity, and the shipped table rendered it
            LITERALLY: a run with no points showed "-Infinity" to the therapist (recorded as a L21
            quirk in the E21 registry). Both columns now go through the guarded helpers, which
            answer null for a run with nothing on it.
          */}
          <Table.Column<Exposition>
            key="maxSud"
            title={t('shared.exposure.highestSud')}
            render={(_, record) => highestSud(record) ?? ''}
          />

          <Table.Column<Exposition>
            key="endSud"
            title={t('shared.exposure.endSud')}
            render={(_, record) => endSud(record) ?? ''}
          />

          {/*
            D-expectancies-2026-10-07 (Q-exp-1 A: ES-ONLY) — Expected (feared outcome, likelihood
            before → after) · Happened · What was learned. An exposition that recorded none of the
            answers shows ONE line across the three instead of blanks.
          */}
          {esRunner ? (
            <Table.Column<Exposition>
              key="expected"
              title={t('shared.exposure.expectedColumn')}
              onCell={(record) =>
                hasExpectancyAnswers(record) ? {} : { colSpan: 3 }
              }
              render={(_, record) =>
                hasExpectancyAnswers(record) ? (
                  <span data-testid="cc-exposure-history-expected">
                    {expectedCellText(record)}
                  </span>
                ) : (
                  <Text
                    type="secondary"
                    data-testid="cc-exposure-history-no-answers"
                  >
                    {t('shared.exposure.noAnswersRecorded')}
                  </Text>
                )
              }
            />
          ) : null}
          {esRunner ? (
            <Table.Column<Exposition>
              key="happened"
              title={t('shared.exposure.happenedColumn')}
              onCell={(record) =>
                hasExpectancyAnswers(record) ? {} : { colSpan: 0 }
              }
              render={(_, record) => (
                <span data-testid="cc-exposure-history-happened">
                  {record?.outcomeOccurred
                    ? t(
                        {
                          yes: 'shared.exposure.outcomeYes',
                          partly: 'shared.exposure.outcomePartly',
                          no: 'shared.exposure.outcomeNo',
                        }[record.outcomeOccurred],
                      )
                    : ''}
                </span>
              )}
            />
          ) : null}
          {esRunner ? (
            <Table.Column<Exposition>
              key="learned"
              title={t('shared.exposure.learnedColumn')}
              onCell={(record) =>
                hasExpectancyAnswers(record) ? {} : { colSpan: 0 }
              }
              render={(_, record) => (
                <span data-testid="cc-exposure-history-learned">
                  {record?.learned ?? ''}
                </span>
              )}
            />
          ) : null}
          {/* E43 — health data: E21 · PortalExpositionHeartRate — the client's heart rate during the run, if shared with you */}
          {esRunner ? (
            <Table.Column<Exposition>
              key="heartRate"
              title={t('shared.exposure.heartRate.column')}
              render={(_, record) =>
                record?.expositionId && clientId ? (
                  <ExpositionHeartRateCell subject={clientId} expositionId={record.expositionId} />
                ) : (
                  ''
                )
              }
            />
          ) : null}

          <Table.Column<Exposure>
            key="reactionNotes"
            dataIndex="reactionNotes"
            title={t('shared.exposure.reactionNotes')}
          />

          <Table.Column<Exposition>
            title={t('resourcesCommon.actions')}
            dataIndex="actions"
            render={(_, record, index) => {
              const gate = canEditExposure(
                clients.find((c) => c._id === clientId),
                {
                  isTherapistAdmin:
                    identity?.db?.role === UserRole.THERAPIST_ADMIN,
                },
              );

              /**
               * THE E-ERA KEEPS THE HALF THE DECIDERS ACCEPT AND DROPS THE HALF THEY DO NOT — the
               * same line WP4 drew on mobile, walked against the deciders rather than a summary.
               *
               * `FinishExposition` explicitly records a later, different finish as a REVISION
               * ("the runner lets a person re-open a run, add points and stop again"), and
               * `RecordExpositionReaction` guards only that the run exists. So RESUME is supported
               * and is offered. What is genuinely gone is amending a recorded point's level or its
               * clock — the shipped Edit button opened the drawer with the whole run editable, and
               * saving that would post an `expositions[]` the decider refuses with
               * `EXPOSITION_EDIT_NOT_PORTED`. Rendering a control that calls something the server
               * will refuse is the one thing this build may not do.
               *
               * The portal has no per-run DELETE (only the per-RUNG one, which IS modelled and
               * keeps its 2-hour window), so nothing had to be hidden for that. DISCARDING a run
               * has no modelled command either way — recorded in the registry as a founder
               * decision owed.
               */
              return (
                <Space>
                  <Tooltip
                    title={
                      esRunner ? t('pages.exposure.pastRunReadOnly') : undefined
                    }
                  >
                    <Button
                      data-testid="cc-create-exposure-situation-edit-exposure"
                      icon={<EditOutlined />}
                      disabled={!gate.enabled}
                      onClick={() => {
                        if (esRunner) {
                          resumeExposure(record, index);
                          return;
                        }
                        const oldValues = form.getFieldValue('expositions');
                        setExpositionIdx(
                          oldValues.findIndex(
                            (e: typeof emptyExposition) =>
                              e.createdAt === record.createdAt,
                          ),
                        );
                        // an EDIT of a run the row already holds: if a flip lands while this
                        // drawer is open the save cannot be re-issued, because amending a recorded
                        // run has no modelled command — the recovery says so out loud instead
                        setLegacyRunIsNew(false);
                        setOpenDrawer(true);
                        const neededExpositon =
                          form.getFieldValue('expositions')?.[
                            oldValues.findIndex(
                              (e: typeof emptyExposition) =>
                                e.createdAt === record.createdAt,
                            )
                          ];

                        exposureForm.setFieldsValue({
                          ...neededExpositon,
                        });
                        setTime(0);
                      }}
                    >
                      {esRunner ? t('shared.exposure.es.resumeRun') : null}
                    </Button>
                  </Tooltip>
                </Space>
              );
            }}
          />
        </Table>

        <Button
          data-testid="cc-create-exposure-situation-start-exposure-bottom"
          type="primary"
          style={{
            marginTop: 20,
          }}
          onClick={startExposure}
          icon={<PlusOutlined />}
        >
          {t('shared.exposure.startExposure')}
        </Button>
      </List>
    </Form>
  );
};
